import React, { Fragment, useCallback } from 'react';
import {
  ElementPropertyType,
  IFactoryProperty,
} from '@shared/models/element-property.model';
import { OneOfPropertyElements } from '@shared/models/project.model';
import { IDataTable } from '@shared/models/data-table.model';
import { getAddPropertyMenuLabel } from '@shared/helpers/element-property-display-name.helpers';
import { labelFromValue } from '@shared/helpers/string.helpers';
import { Menu } from '@ui/menus/Menu';
import {
  useAddProperty,
  useOptionalProperties,
} from '@client/hooks/use-element-properties.hooks';
import {
  useApplyDataTableProperties,
  useAvailableDataTables,
} from '@features/data-table/data-table.hooks';

type ElementPropertyMenuItemsProps = {
  element: OneOfPropertyElements;
  onItemSelect?: () => void;
};

export const ElementPropertyMenuItems: React.FC<
  ElementPropertyMenuItemsProps
> = ({ element, onItemSelect }) => {
  const optionalProperties = useOptionalProperties(element);
  const availableDataTables = useAvailableDataTables(element);
  const addProperty = useAddProperty(element);
  const addDataTableProperties = useApplyDataTableProperties(element);

  const close = useCallback(() => onItemSelect?.(), [onItemSelect]);

  const handleAddProperty = useCallback(
    (factoryProperty?: IFactoryProperty) => {
      addProperty(factoryProperty);
      close();
    },
    [addProperty, close],
  );

  const handleAddDataTable = useCallback(
    (dataTable: IDataTable) => {
      addDataTableProperties(dataTable);
      close();
    },
    [addDataTableProperties, close],
  );

  return (
    <>
      {availableDataTables.length > 0 && (
        <>
          <Menu.Group label="Connect data table">
            {availableDataTables.map((table) => (
              <Menu.Item
                key={table.id}
                onClick={() => handleAddDataTable(table)}
                searchText={table.name}
              >
                {labelFromValue(table.name)}
              </Menu.Item>
            ))}
          </Menu.Group>
          <Menu.Divider />
        </>
      )}

      <Menu.Group label="Add property">
        {optionalProperties.map((property) => (
          <Fragment key={property.id}>
            <Menu.Item
              onClick={() => handleAddProperty(property)}
              searchText={getAddPropertyMenuLabel(property)}
            >
              {getAddPropertyMenuLabel(property)}
            </Menu.Item>
            <Menu.Divider />
          </Fragment>
        ))}
        <Menu.Item onClick={() => handleAddProperty()}>Number</Menu.Item>
        <Menu.Item
          onClick={() =>
            handleAddProperty({
              type: ElementPropertyType.Switch,
              count: true,
            })
          }
        >
          Toggle
        </Menu.Item>
      </Menu.Group>
    </>
  );
};

type ElementPropertyMenuProps = {
  anchor?: Element | null;
  element: OneOfPropertyElements;
  onClose: () => void;
  menuWidth?: number;
};

export const ElementPropertyMenu: React.FC<ElementPropertyMenuProps> = ({
  anchor,
  element,
  onClose,
  menuWidth = 280,
}) => (
  <Menu
    open={Boolean(anchor)}
    anchorEl={anchor}
    onClose={onClose}
    width={menuWidth}
  >
    <ElementPropertyMenuItems element={element} onItemSelect={onClose} />
  </Menu>
);
